import { 
  Stethoscope, 
  Tag, 
  Heart, 
  Car, 
  Package, 
  BookOpen,
  Zap,
  ArrowRight,
  Activity,
  Wrench,
  Building2,
  Droplets
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

export const SpecializedSolutions = () => {
  const { t } = useLanguage();

  const specializedSolutions = [
    {
      id: 'real-estate',
      title: t('specialized.realEstate'),
      subtitle: t('specialized.realEstateSub'),
      description: t('specialized.realEstateDesc'),
      icon: Building2,
      color: 'bg-slate-800',
      link: '/real-estate',
      internal: true,
      inverted: true,
      isNew: true,
    },
    {
      id: 'e2-insurance',
      title: t('specialized.e2Insurance'),
      subtitle: t('specialized.e2InsuranceSub'),
      description: t('specialized.e2InsuranceDesc'),
      icon: Droplets,
      color: 'bg-slate-800',
      link: '/e2-insurance',
      internal: true,
      inverted: true,
      isNew: true,
    },
    {
      id: 'klinik-track',
      title: t('specialized.klinikTrack'),
      subtitle: t('specialized.klinikTrackSub'),
      description: t('specialized.klinikTrackDesc'),
      icon: Stethoscope,
      color: 'bg-blue-500',
      link: 'https://kliniktrack.com',
      external: true,
    },
    {
      id: 'stick-go',
      title: t('specialized.stickGo'),
      subtitle: t('specialized.stickGoSub'),
      description: t('specialized.stickGoDesc'),
      icon: Tag,
      color: 'bg-orange-500',
      link: 'https://stickandgo.0gbaltics.com',
      external: true,
    },
    {
      id: 'tool-tracking',
      title: t('specialized.toolTracking'),
      subtitle: t('specialized.toolTrackingSub'),
      description: t('specialized.toolTrackingDesc'),
      icon: Wrench,
      color: 'bg-amber-600',
      link: '/tool-tracking',
      internal: true,
    },
    {
      id: 'eldera',
      title: t('specialized.eldera'),
      subtitle: t('specialized.elderaSub'),
      description: t('specialized.elderaDesc'),
      icon: Heart,
      color: 'bg-rose-500',
      link: 'https://eldera.lv',
      external: true,
    },
    {
      id: 'smart-parking',
      title: t('specialized.smartParking'),
      subtitle: t('specialized.smartParkingSub'),
      description: t('specialized.smartParkingDesc'),
      icon: Car,
      color: 'bg-violet-500',
      link: '#contact',
      external: false,
    },
    {
      id: 'pakuskapis',
      title: t('specialized.pakuskapis'),
      subtitle: t('specialized.pakuskapisSub'),
      description: t('specialized.pakuskapisDesc'),
      icon: Package,
      color: 'bg-teal-500',
      link: 'https://pakuskapis.lv',
      external: true,
    },
    {
      id: 'knygomatai',
      title: t('specialized.bibliomats'),
      subtitle: t('specialized.bibliomatsSub'),
      description: t('specialized.bibliomatsDesc'),
      icon: BookOpen,
      color: 'bg-amber-500',
      link: 'https://bibliomats.lv',
      external: true,
    },
    {
      id: 'smart-energy',
      title: t('specialized.smartEnergy'),
      subtitle: t('specialized.smartEnergySub'),
      description: t('specialized.smartEnergyDesc'),
      icon: Zap,
      color: 'bg-green-500',
      link: '/building-energy-efficiency',
      internal: true,
    },
    {
      id: 'medical-devices',
      title: t('specialized.medicalDevices'),
      subtitle: t('specialized.medicalDevicesSub'),
      description: t('specialized.medicalDevicesDesc'),
      icon: Activity,
      color: 'bg-red-500',
      link: 'https://e-health.0gbaltics.com',
      external: true,
    },
  ];

  const handleClick = (solution) => {
    if (solution.external) {
      window.open(solution.link, '_blank');
    } else if (!solution.internal) {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="partners" className="py-20 md:py-32 bg-slate-50" data-testid="specialized-solutions-section">
      <div className="container-0g">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <p className="section-label">{t('specialized.label')}</p>
          <h2 className="section-title mb-4">{t('specialized.title')}</h2>
          <p className="text-lg text-slate-600 max-w-2xl">
            {t('specialized.subtitle')}
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specializedSolutions.map((solution) => {
            const Icon = solution.icon;
            const isInverted = solution.inverted;
            const CardContent = (
              <>
                {solution.isNew && (
                  <span
                    className="absolute top-3 right-3 z-10 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-gradient-to-r from-teal-400 to-cyan-300 text-slate-900 shadow-lg shadow-teal-500/40 animate-pulse-premium"
                    data-testid="new-badge"
                  >
                    {t('specialized.newBadge')}
                  </span>
                )}
                {/* Icon with colored background */}
                <div className={`w-12 h-12 rounded-sm ${isInverted ? 'bg-white' : solution.color} flex items-center justify-center mb-4`}>
                  <Icon className={`h-6 w-6 ${isInverted ? 'text-slate-900' : 'text-white'}`} />
                </div>
                
                {/* Title */}
                <h3 className={`text-xl font-semibold mb-1 ${isInverted ? 'text-white' : 'text-slate-900'}`}>
                  {solution.title}
                </h3>
                
                {/* Subtitle */}
                <p className={`text-sm font-medium mb-3 ${isInverted ? 'text-teal-300' : 'text-teal-600'}`}>
                  {solution.subtitle}
                </p>
                
                {/* Description */}
                <p className={`text-sm mb-4 ${isInverted ? 'text-slate-300' : 'text-slate-600'}`}>
                  {solution.description}
                </p>
                
                {/* Link */}
                <div className={`flex items-center font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity ${isInverted ? 'text-teal-300' : 'text-teal-600'}`}>
                  <span>{solution.external ? t('specialized.visitWebsite') : t('solutions.learnMore')}</span>
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </>
            );

            const baseClass = isInverted
              ? 'relative group bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-teal-500/30 rounded-sm p-6 hover:border-teal-400 hover:shadow-2xl hover:shadow-teal-500/20 transition-all cursor-pointer block ring-1 ring-teal-500/10'
              : 'relative group bg-white border border-slate-200 rounded-sm p-6 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer block';

            if (solution.internal) {
              return (
                <Link
                  key={solution.id}
                  to={solution.link}
                  className={baseClass}
                  data-testid={`specialized-${solution.id}`}
                >
                  {CardContent}
                </Link>
              );
            }

            return (
              <div
                key={solution.id}
                className={baseClass}
                onClick={() => handleClick(solution)}
                data-testid={`specialized-${solution.id}`}
              >
                {CardContent}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
